import { ColorPicker as ChakraColorPicker } from '@chakra-ui/react';
import React, { forwardRef, useEffect, useState } from 'react';

import Button from 'ui/Button';
import { Box } from 'ui/Layout';
import Tooltip from 'ui/Tooltip';
import { hasPressedArrowKey } from 'utils/keyboard';

import Icon from './Icon';

export { parseColor } from '@chakra-ui/react';

export type Color = {
  value: string;
  label: string;
  keywords: string[];
  isDefault?: boolean;
};

export type ColorGroup = {
  label: string;
  colors: Color[];
  actionButton?: React.ReactNode;
};

export type ColorPickerView = 'preset' | 'custom';

export type ColorPickerProps = Omit<
  ChakraColorPicker.RootProps,
  'size' | 'variant'
> & {
  presetColorGroups?: ColorGroup[];
  view?: ColorPickerView;
  setView?: (view: ColorPickerView) => void;
};

const ColorPicker = forwardRef<HTMLDivElement, ColorPickerProps>(
  ({ presetColorGroups, view = 'custom', setView, ...rest }, ref) => {
    const [open, setOpen] = useState(false);

    // Reset the view when the popover is closed, depending on whether preset colors are available
    useEffect(() => {
      if (!open) {
        setView?.(presetColorGroups ? 'preset' : 'custom');
      }
    }, [open]);

    return (
      <ChakraColorPicker.Root
        ref={ref}
        closeOnSelect
        openAutoFocus={false}
        open={open}
        onOpenChange={(e) => setOpen(e.open)}
        // This prevents the popover from closing when clicking INSIDE it (this method is a bit
        // of a misnomer; the popover will still close when clicking outside it which is handled
        // by the default behavior of the onPointerDownOutside method)
        onFocusOutside={(e) => e.preventDefault()}
        size="xl"
        {...rest}
      >
        <ChakraColorPicker.Control>
          <ChakraColorPicker.Trigger
            data-fit-content
            cursor="pointer"
            rounded="md"
            _focusVisible={{
              focusRing: 'mixed',
              focusRingColor: 'blue.500',
            }}
          >
            <ChakraColorPicker.ValueSwatch rounded="inherit" />
          </ChakraColorPicker.Trigger>
        </ChakraColorPicker.Control>
        <ChakraColorPicker.Positioner>
          <ChakraColorPicker.Content
            border="1px solid"
            borderColor="gray.500"
            borderRadius="md"
            boxShadow="sm"
            gap={3}
            p={3}
            w="234px"
            _open={{ animation: 'none' }}
            _closed={{ animation: 'none' }}
          >
            {view === 'preset' && (
              <ColorPickerPresetView colorGroups={presetColorGroups ?? []} />
            )}
            {view === 'custom' && (
              <ColorPickerCustomView onUseClick={() => setOpen(false)} />
            )}
          </ChakraColorPicker.Content>
        </ChakraColorPicker.Positioner>
        <ChakraColorPicker.HiddenInput tabIndex={-1} />
      </ChakraColorPicker.Root>
    );
  }
);

type ColorPickerPresetViewProps = {
  colorGroups: ColorGroup[];
};

const ColorPickerPresetView: React.FC<ColorPickerPresetViewProps> = ({
  colorGroups,
}) => (
  <>
    {colorGroups?.map(({ label, colors, actionButton }) => (
      <Box key={label}>
        <ChakraColorPicker.Label
          color="gray.900"
          display="block"
          fontSize="xs"
          lineHeight="14px"
          mb={1.5}
        >
          {label}
        </ChakraColorPicker.Label>
        <ChakraColorPicker.SwatchGroup>
          {colors.map(({ label, value }, index) => (
            <ChakraColorPicker.SwatchTrigger
              key={`${value}-${index}`}
              value={value}
              _focusVisible={{
                focusRing: 'mixed',
                focusRingColor: 'blue.500',
              }}
            >
              <Tooltip content={label || value}>
                <ChakraColorPicker.Swatch
                  boxSize={7}
                  cursor="pointer"
                  value={value}
                >
                  <ChakraColorPicker.SwatchIndicator>
                    <Icon icon="check" />
                  </ChakraColorPicker.SwatchIndicator>
                </ChakraColorPicker.Swatch>
              </Tooltip>
            </ChakraColorPicker.SwatchTrigger>
          ))}
          {actionButton}
        </ChakraColorPicker.SwatchGroup>
      </Box>
    ))}
  </>
);

type ColorPickerCustomViewProps = {
  onUseClick: () => void;
};

const ColorPickerCustomView: React.FC<ColorPickerCustomViewProps> = ({
  onUseClick,
}) => (
  <>
    <ChakraColorPicker.Area cursor="pointer" overflow="visible">
      <ChakraColorPicker.AreaBackground />
      <ChakraColorPicker.AreaThumb boxSize={4} />
    </ChakraColorPicker.Area>
    <ChakraColorPicker.ChannelSlider channel="hue" cursor="pointer">
      <ChakraColorPicker.ChannelSliderTrack h={2} />
      <ChakraColorPicker.ChannelSliderThumb
        boxSize={4}
        focusRingColor="blue.500"
        // This prevents controlling the slider value with the keyboard
        // See https://github.com/sankeyart/sankeyart/issues/1966
        onKeyDown={(ev) => {
          if (hasPressedArrowKey(ev)) {
            ev.preventDefault();
          }
        }}
      />
    </ChakraColorPicker.ChannelSlider>
    <ChakraColorPicker.ChannelInput
      background="white"
      border="1px solid"
      borderColor="gray.400"
      channel="hex"
      focusRing="mixed"
      focusRingColor="blue.500"
      focusRingWidth="1px"
      fontSize="sm"
      paddingLeft={2}
      h={7}
    />
    <Button onClick={onUseClick} variant="neutral-solid" size="sm">
      Use color
    </Button>
  </>
);

export default ColorPicker;
